/* eslint-disable camelcase */
/* eslint react-hooks/exhaustive-deps: 0 */
import React, {
  forwardRef,
  useImperativeHandle,
  useEffect,
  useCallback,
  useLayoutEffect,
  useState,
} from 'react';
import Section from '../Section';
import { useInputState, useAddressQuery, useUfs } from '../../hooks';
import { Row } from '../../styles/utils/Grid';
import Input from '../Input';
import masks from '../../utils/masks';
import { required, validCep } from '../../utils/inputValidation';
import * as S from './styled';
import Autocomplete from '../Autocomplete';
import {
  addressDataTitle,
  addressDataSubtitle,
  cepPlaceholder,
  addressPlaceholder,
  numberPlaceholder,
  additionPlaceholder,
  neighborhoodPlaceholder,
  cityPlaceholder,
  ufPlaceholder,
  forgotCepText,
} from '../../utils/constants';

const AddressData = ({ addressData, setContext, shouldQuery }, ref) => {
  const { loading: ufsLoading, ufs } = useUfs();
  const [cep, setCep, cepRef, validateCep] = useInputState(addressData?.cep);
  const [previousCep, setPreviousCep] = useState(addressData?.cep);
  const [address, setAddress, addressRef, validateAddress] = useInputState(
    addressData?.logradouro
  );
  const [number, setNumber, numberRef, validateNumber] = useInputState(
    addressData?.numero
  );
  const [uf, setUf, ufRef, validateUf] = useInputState(addressData?.estado);
  const [
    neighborhood,
    setNeighborhood,
    neighborhoodRef,
    validateNeighborhood,
  ] = useInputState(addressData?.bairro);
  const [city, setCity, cityRef, validateCity] = useInputState(
    addressData?.cidade
  );
  const [addition, setAddition, additionRef, validateAddition] = useInputState(
    addressData?.complemento
  );
  const { getAddress, loading } = useAddressQuery();
  const forgotCep = () => {
    window.open(
      'http://www.buscacep.correios.com.br/sistemas/buscacep/',
      '_blank',
      'width=795, height=590, top=100, left=699, status=no, toolbar=no, menubar=no, resizable=no, fullscreen=no'
    );
  };

  const handleInputChange = setter => value => {
    setter(value);
  };

  useImperativeHandle(
    ref,
    () => ({
      validate: () => {
        const isInvalid = [
          validateCep,
          validateAddress,
          validateNumber,
          validateAddition,
          validateNeighborhood,
          validateCity,
          validateUf,
        ]
          .reverse()
          .reduce((acc, v) => {
            const invalid = v();
            return acc || invalid;
          }, false);
        return {
          invalid: isInvalid,
          value: isInvalid
            ? ''
            : {
                endereco: {
                  cep: cep.value,
                  estado: uf.value,
                  cidade: city.value,
                  bairro: neighborhood.value,
                  logradouro: address.value,
                  numero: number.value,
                  complemento: addition.value,
                },
              },
        };
      },
    }),
    [validateCep]
  );

  useLayoutEffect(() => {
    setContext({
      loading: [loading, ufsLoading].some(current => current),
    });
  }, [loading, setContext, ufsLoading]);

  const onCepFilled = useCallback(async () => {
    if (cep.value === previousCep && !shouldQuery) {
      return;
    }
    setPreviousCep(cep.value);
    const {
      nome_bairro,
      indice_tipo_logradouro,
      nome_localidade,
      codigo_uf,
    } = await getAddress(cep.value);

    if (indice_tipo_logradouro == null) {
      setCep({
        value: '',
        invalid: true,
        errorMessage: 'CEP inválido',
      });
      setNumber({ ...number, value: '' });
      setNeighborhood({ ...neighborhood, value: '' });
      setCity({ ...city, value: '' });
      setAddress({ ...address, value: '' });
      setUf({ ...uf, value: '' });
    } else {
      setUf({ invalid: false, errorMessage: '', value: codigo_uf });
      setNeighborhood({ invalid: false, errorMessage: '', value: nome_bairro });
      setCity({ invalid: false, errorMessage: '', value: nome_localidade });
      setAddress({
        invalid: false,
        errorMessage: '',
        value: indice_tipo_logradouro,
      });
      if (document.activeElement.id === 'cep') {
        numberRef.current.focus();
      }
    }
  }, [cep.value]);

  useEffect(() => {
    if (cep.value.length === 8) {
      onCepFilled();
    }
  }, [cep.value, onCepFilled]);

  return (
    <Section
      title={addressDataTitle}
      data-blocoid="Bloco conteúdo: Endereço"
      subTitle={addressDataSubtitle}
    >
      <Row hasBorderBottom flexStart columns={3} margin="30px 0 10px 0">
        <Input
          placeholder={cepPlaceholder}
          id="cep"
          value={cep.value}
          mask={masks.cep}
          onChange={handleInputChange(setCep)}
          ref={cepRef}
          invalid={cep.invalid}
          errorMessage={cep.errorMessage}
          validations={[required, validCep]}
          type="tel"
          maxLength="9"
        />

        <S.StyledAnchor onClick={() => forgotCep()}>
          {forgotCepText}
        </S.StyledAnchor>
      </Row>

      <Row>
        <Input
          placeholder={addressPlaceholder}
          value={address.value}
          errorMessage={address.errorMessage}
          invalid={address.invalid}
          name="logradouro"
          onChange={handleInputChange(setAddress)}
          ref={addressRef}
          validations={[required]}
          maxLength={50}
        />
      </Row>
      <Row columns={2}>
        <Input
          placeholder={numberPlaceholder}
          value={number.value}
          onChange={handleInputChange(setNumber)}
          mask={masks.numberOnly}
          ref={numberRef}
          invalid={number.invalid}
          errorMessage={number.errorMessage}
          validations={[required]}
          maxLength={15}
          type="tel"
        />

        <Input
          placeholder={additionPlaceholder}
          value={addition.value}
          onChange={handleInputChange(setAddition)}
          ref={additionRef}
          invalid={addition.invalid}
          errorMessage={addition.errorMessage}
          maxLength={100}
        />
      </Row>
      <Row columns={3}>
        <Input
          placeholder={neighborhoodPlaceholder}
          value={neighborhood.value}
          onChange={handleInputChange(setNeighborhood)}
          ref={neighborhoodRef}
          invalid={neighborhood.invalid}
          errorMessage={neighborhood.errorMessage}
          validations={[required]}
          maxLength={30}
        />

        <Input
          placeholder={cityPlaceholder}
          value={city.value}
          errorMessage={city.errorMessage}
          invalid={city.invalid}
          onChange={handleInputChange(setCity)}
          ref={cityRef}
          validations={[required]}
          maxLength={30}
        />

        <Autocomplete
          options={ufs}
          placeholder={ufPlaceholder}
          value={uf.value}
          errorMessage={uf.errorMessage}
          invalid={uf.invalid}
          validations={[required]}
          getOptionLabel={option => option.codigo}
          getOptionValue={option => option.codigo}
          setter={setUf}
          ref={ufRef}
        />
      </Row>
    </Section>
  );
};

export default forwardRef(AddressData);
